import { useState, useMemo, useRef } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function AccountSwitcher() {
  const { accounts, currentAccount, setCurrentAccountId } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const sorted = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );

  const filtered = useMemo(() => {
    if (!search) return sorted;
    const q = search.toLowerCase();
    return sorted.filter((a) => a.name.toLowerCase().includes(q));
  }, [sorted, search]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch('');
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleSelect = (id: string) => {
    if (id !== currentAccount?.id) {
      setCurrentAccountId(id);
      handleClose();
      navigate('/');
      // Reload so all data refetches under the new account
      window.location.reload();
    } else {
      handleClose();
    }
  };

  if (accounts.length <= 1) {
    return (
      <Typography
        variant="body2"
        sx={{ color: '#374151', fontWeight: 600, fontSize: '0.85rem' }}
      >
        {currentAccount?.name}
      </Typography>
    );
  }

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          px: 1.5,
          py: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: anchorEl ? '#10A4C2' : '#E5E7EB',
          bgcolor: anchorEl ? 'rgba(16,164,194,0.04)' : 'transparent',
          transition: 'all 0.15s',
          '&:hover': {
            borderColor: '#10A4C2',
            bgcolor: 'rgba(16,164,194,0.04)',
          },
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', maxWidth: 200 }}
        >
          {currentAccount?.name}
        </Typography>
        <UnfoldMoreIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 300, mt: 0.5, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
          },
        }}
        TransitionProps={{
          onEntered: () => searchRef.current?.focus(),
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid #E5E7EB' }}>
          <TextField
            inputRef={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search accounts..."
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.85rem' },
              },
            }}
          />
        </Box>

        <List sx={{ maxHeight: 320, overflow: 'auto', py: 0.5 }}>
          {filtered.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 1.5, fontSize: '0.85rem' }}
            >
              No accounts found
            </Typography>
          ) : (
            filtered.map((account) => {
              const isActive = account.id === currentAccount?.id;
              return (
                <ListItemButton
                  key={account.id}
                  onClick={() => handleSelect(account.id)}
                  sx={{
                    mx: 0.5,
                    borderRadius: 1,
                    py: 0.75,
                    bgcolor: isActive ? 'rgba(16,164,194,0.08)' : undefined,
                    '&:hover': { bgcolor: isActive ? 'rgba(16,164,194,0.12)' : undefined },
                  }}
                >
                  <ListItemText
                    primary={account.name}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#102B3D' : '#374151',
                    }}
                  />
                  {isActive && <CheckIcon sx={{ fontSize: 18, color: '#10A4C2' }} />}
                </ListItemButton>
              );
            })
          )}
        </List>
      </Popover>
    </>
  );
}
